/**
 * SA-439: the admin editor for a widget's offered pages (spec §9).
 *
 * A destination is a page of the host site the widget's assistant may offer
 * a visitor: {key, title, url, purpose}. Pure controlled component — no
 * fetching, no widget-shaped state — so it stays independent of
 * WidgetManagementPage's own fixtures (SA-441 is about to restructure that
 * page into tabs).
 */

import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { WidgetDestination } from '@/hooks/useWidgets'

export interface WidgetDestinationsEditorProps {
  value: WidgetDestination[]
  onChange: (value: WidgetDestination[]) => void
}

const EMPTY_DESTINATION: WidgetDestination = { key: '', title: '', url: '', purpose: '' }

const FIELD_LABEL_CLASS = 'block text-sm font-medium text-foreground mb-1'
const FIELD_INPUT_CLASS = 'w-full border border-border rounded-lg px-3 py-2 text-sm bg-card'

/** SA-439: one source for the copy so the form and the server sentence cannot drift. */
export const DESTINATIONS_HINT =
  "Pages this widget's assistant may offer a visitor. A destination's URL must be on one of this widget's site origins, above."

export function WidgetDestinationsEditor({ value = [], onChange }: WidgetDestinationsEditorProps) {
  const updateRow = (index: number, field: keyof WidgetDestination, fieldValue: string) => {
    onChange(value.map((row, i) => (i === index ? { ...row, [field]: fieldValue } : row)))
  }

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const addRow = () => {
    onChange([...value, { ...EMPTY_DESTINATION }])
  }

  return (
    <div className="space-y-3">
      <div>
        <div className={FIELD_LABEL_CLASS}>Offered pages</div>
        <p className="text-xs text-muted-foreground">{DESTINATIONS_HINT}</p>
      </div>
      {value.map((destination, index) => (
        <div key={index} className="flex flex-wrap items-end gap-2 border border-border rounded-lg p-3">
          <div className="flex-1 min-w-[100px]">
            <label htmlFor={`widget-destination-key-${index}`} className={FIELD_LABEL_CLASS}>Key</label>
            <input
              id={`widget-destination-key-${index}`}
              type="text"
              className={FIELD_INPUT_CLASS}
              value={destination.key}
              onChange={(e) => updateRow(index, 'key', e.target.value)}
              aria-label={`Key, destination ${index + 1}`}
            />
          </div>
          <div className="flex-[2] min-w-[140px]">
            <label htmlFor={`widget-destination-title-${index}`} className={FIELD_LABEL_CLASS}>Title</label>
            <input
              id={`widget-destination-title-${index}`}
              type="text"
              className={FIELD_INPUT_CLASS}
              value={destination.title}
              onChange={(e) => updateRow(index, 'title', e.target.value)}
              aria-label={`Title, destination ${index + 1}`}
            />
          </div>
          <div className="flex-[3] min-w-[180px]">
            <label htmlFor={`widget-destination-url-${index}`} className={FIELD_LABEL_CLASS}>URL</label>
            <input
              id={`widget-destination-url-${index}`}
              type="text"
              className={FIELD_INPUT_CLASS}
              value={destination.url}
              onChange={(e) => updateRow(index, 'url', e.target.value)}
              aria-label={`URL, destination ${index + 1}`}
            />
          </div>
          <div className="flex-[2] min-w-[140px]">
            <label htmlFor={`widget-destination-purpose-${index}`} className={FIELD_LABEL_CLASS}>Purpose</label>
            <input
              id={`widget-destination-purpose-${index}`}
              type="text"
              className={FIELD_INPUT_CLASS}
              value={destination.purpose}
              onChange={(e) => updateRow(index, 'purpose', e.target.value)}
              aria-label={`Purpose, destination ${index + 1}`}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Remove destination ${index + 1}`}
            onClick={() => removeRow(index)}
          >
            <Trash2 className="w-3.5 h-3.5 text-destructive" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="secondary" size="sm" onClick={addRow} leftIcon={<Plus className="w-4 h-4" />}>
        Add destination
      </Button>
    </div>
  )
}
